import React, { useEffect, useState } from "react";
import client, { apiErr } from "../lib/api";
import { useApp } from "../context/AppContext";
import { Button, Input, Select, Card, Modal, Badge } from "../components/ui";
import { LANGS, APP_FULL, APP_VERSIONS, tp } from "../i18n";
import { APP_LOGOS, APP_WORDMARKS } from "../appLogos";
import { downloadManual, openManual } from "../lib/manuals";
import ManualEditorModal from "../components/ManualEditorModal";
import ContactsMessaging from "../components/ContactsMessaging";
import { useBranding } from "../components/BrandFooter";
import ClientsSection from "../components/ClientsSection";
import ClusterInvites from "../components/ClusterInvites";
import TierRoster from "../components/TierRoster";
import OlosoPanel from "../components/OlosoPanel";
import DemoAccountsPanel from "../components/DemoAccountsPanel";
import RepairDispatch from "../components/RepairDispatch";
import { Plus, Eye, EyeOff, Send, KeyRound, RotateCcw, Lock, Trash2, ShieldCheck, MessageSquare, CreditCard, Percent, BookOpen, FileText, Pencil, CheckCircle2, Copy, Check, Download, AlertTriangle } from "lucide-react";
import ProfileForm from "../components/ProfileForm";
import { LiveBackupIndicator } from "../components/LiveBackup";
import SpecialManuals from "../components/SpecialManuals";

const APP_OPTIONS = [
  { v: "family_tree", l: APP_FULL.family_tree },
  { v: "benefits", l: APP_FULL.benefits },
  { v: "currency", l: APP_FULL.currency },
  { v: "holdings", l: APP_FULL.holdings },
];
const OS = [{ v: "WI", l: "Windows (WI)" }, { v: "AP", l: "Apple (AP)" }, { v: "AN", l: "Android (AN)" }];

export default function MasterEnabler() {
  const { isAdmin, isSupport, can, lang } = useApp();
  const branding = useBranding();
  const scMap = branding?.system_consent_content;
  const systemConsentText = (scMap && (scMap[lang] || scMap.ENG)) ||
    "I agree to accept system generated and program-related messages from the vendor from time to time.";
  const ownerView = isAdmin || isSupport;
  const [list, setList] = useState([]);
  const [open, setOpen] = useState(false);
  const [reveal, setReveal] = useState({}); // id -> full code
  const [recOpen, setRecOpen] = useState(false);
  const [lockFor, setLockFor] = useState(null);
  const [actFor, setActFor] = useState(null);
  const [actCode, setActCode] = useState("");
  const [msg, setMsg] = useState("");
  const [form, setForm] = useState({ app_type: "family_tree", email: "", phone: "", language_code: "ENG", os_code: "WI", device_class: "desktop", instance_name: "" });
  const [rec, setRec] = useState({ email: "", phone: "", first_ten: "", device_class: "desktop" });
  const [newLock, setNewLock] = useState("");
  const [testOpen, setTestOpen] = useState(false);
  const [testPhone, setTestPhone] = useState("");
  const [testResult, setTestResult] = useState(null);
  const [pricing, setPricing] = useState(null);
  const [welcome, setWelcome] = useState(null);   // post-checkout: { licenses, kind }
  const [copied, setCopied] = useState("");
  const [acct, setAcct] = useState(null);
  useEffect(() => { client.get("/account/purchases").then((r) => setAcct(r.data)).catch(() => {}); }, []);
  const requireProfile = !!(acct && acct.source === "storefront" && !acct.profile_complete);
  const storeLinks = branding?.store_links || {};
  const copyCode = (code) => { navigator.clipboard?.writeText(code).catch(() => {}); setCopied(code); setTimeout(() => setCopied(""), 1500); };

  // Per-visit agreement + manual viewer
  const [manualApp, setManualApp] = useState("family_tree");
  const [manualLang, setManualLang] = useState(lang || "ENG");
  const [manualVer, setManualVer] = useState(null);
  const [termsChk, setTermsChk] = useState(false);
  const [sysChk, setSysChk] = useState(false);
  const [marketing, setMarketing] = useState(null); // true | false | null
  const [agreed, setAgreed] = useState(false);
  const [editorOpen, setEditorOpen] = useState(false);
  const [reminderOpen, setReminderOpen] = useState(() => !isAdmin && !isSupport && !sessionStorage.getItem("me_reminder_seen"));
  const dismissReminder = () => { sessionStorage.setItem("me_reminder_seen", "1"); setReminderOpen(false); };

  useEffect(() => { client.get("/manual/version").then((r) => setManualVer(r.data)).catch(() => {}); }, []);

  // Both mandatory boxes (and, for runners, a marketing choice) must be set, then confirmed, each visit.
  const canConfirm = termsChk && sysChk && (ownerView || marketing !== null);

  const confirmConsent = async () => {
    try {
      await client.post("/consent", {
        terms_accepted: termsChk, system_msgs_accepted: sysChk,
        marketing_opt_in: ownerView ? null : marketing,
        lang: manualLang, manual_version: manualVer?.version,
      });
      setAgreed(true);
      setMsg("Agreement recorded for this session. You may now send/transmit codes.");
    } catch (e) { setMsg(apiErr(e)); }
  };

  useEffect(() => { client.get("/payments/pricing").then((r) => setPricing(r.data)).catch(() => {}); }, []);
  const priceFor = (app) => pricing?.apps?.[app];
  const cur = (pricing?.currency || "usd").toUpperCase();

  const sendTestSms = async () => {
    setTestResult(null);
    try {
      const { data } = await client.post("/test-sms", { phone: testPhone });
      setTestResult(data);
    } catch (e) { setTestResult({ status: "error", preview: apiErr(e) }); }
  };

  const load = () => client.get(ownerView ? "/licenses" : "/my-licenses").then((r) => setList(r.data)).catch(() => setList([]));
  useEffect(() => { load(); }, []);

  // Handle return from Stripe checkout (?session_id=... unlock / ?service_session_id=... renewal)
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const sid = params.get("session_id");
    if (sid) { setMsg("Verifying payment…"); pollPayment(sid, 0); }
    const ssid = params.get("service_session_id");
    if (ssid) { setMsg("Verifying renewal…"); pollService(ssid, 0); }
    // eslint-disable-next-line
  }, []);

  const pollService = async (sid, attempts) => {
    if (attempts >= 8) { setMsg("Renewal status check timed out. If you completed payment, refresh shortly."); return; }
    try {
      const { data } = await client.get(`/payments/status/${sid}`);
      if (data.payment_status === "paid") {
        setMsg("Service renewed — thank you. Your annual term has been extended by one year.");
        window.history.replaceState({}, "", "/app/enabler"); load(); return;
      }
      if (data.status === "expired") { setMsg("Renewal session expired. Please try again."); window.history.replaceState({}, "", "/app/enabler"); return; }
      setMsg("Renewal is being processed…");
      setTimeout(() => pollService(sid, attempts + 1), 2000);
    } catch (e) { setMsg(apiErr(e)); }
  };

  const pollPayment = async (sid, attempts) => {
    if (attempts >= 8) { setMsg("Payment status check timed out. If you completed payment, refresh shortly."); return; }
    try {
      const { data } = await client.get(`/payments/status/${sid}`);
      if (data.payment_status === "paid") {
        window.history.replaceState({}, "", "/app/enabler");
        load();
        const lics = data.licenses && data.licenses.length ? data.licenses : (data.license ? [data.license] : []);
        if (lics.length) { setWelcome({ licenses: lics, kind: data.kind }); setMsg(""); }
        else { setMsg("Payment successful — instance unlocked."); }
        return;
      }
      if (data.status === "expired") { setMsg("Payment session expired. Please try again."); window.history.replaceState({}, "", "/app/enabler"); return; }
      setMsg("Payment is being processed…");
      setTimeout(() => pollPayment(sid, attempts + 1), 2000);
    } catch (e) { setMsg(apiErr(e)); }
  };

  const buy = async (licId) => {
    setMsg("Redirecting to secure checkout…");
    try {
      const { data } = await client.post("/payments/checkout", { license_id: licId, origin_url: window.location.origin });
      window.location.href = data.url;
    } catch (e) { setMsg(apiErr(e)); }
  };

  const renewService = async (licId) => {
    setMsg("Redirecting to secure renewal checkout…");
    try {
      const { data } = await client.post("/payments/service-checkout", { license_id: licId, origin_url: window.location.origin });
      window.location.href = data.url;
    } catch (e) { setMsg(apiErr(e)); }
  };

  const create = async () => {
    if (!agreed) { setMsg("Please confirm the agreement above before generating or transmitting codes."); setOpen(false); return; }
    setMsg("");
    try {
      const { data } = await client.post("/licenses", form);
      setMsg(`Instance created. First 10 characters disclosed via email & SMS: ${data.license.first_ten}`);
      setOpen(false); load();
    } catch (e) { setMsg(apiErr(e)); }
  };

  const revealFull = async (id) => {
    if (reveal[id]) { setReveal((r) => ({ ...r, [id]: null })); return; }
    const lic = list.find((x) => x.id === id);
    if (lic && !lic.paid) {
      if (!window.confirm("This instance is UNPAID. Activation codes should normally be released only after payment is confirmed. Reveal the full code anyway?")) return;
    }
    const { data } = await client.post(`/licenses/${id}/reveal-full`);
    setReveal((r) => ({ ...r, [id]: data.full_code }));
  };

  const disclose = async (id) => {
    if (!agreed) { setMsg("Please confirm the agreement above before sending codes."); return; }
    const { data } = await client.post(`/licenses/${id}/disclose-first10`);
    const st = data.deliveries.map((d) => `${d.channel}:${d.status}`).join(", ");
    setMsg(`First 10 (${data.first_ten}) re-sent → ${st}`);
  };

  const resend = async (id) => {
    if (!agreed) { setMsg("Please confirm the agreement above before sending codes."); return; }
    try {
      const { data } = await client.post(`/licenses/${id}/resend`);
      const ds = data.delivery_status || {};
      setMsg(`Re-sent ${data.part === "full" ? "full code" : "first 10"} to ${data.sent_to.phone} → SMS: ${ds.sms || "n/a"}, Email: ${ds.email || "n/a"}`);
      load();
    } catch (e) { setMsg(apiErr(e)); }
  };

  const [resendingAll, setResendingAll] = useState(false);
  const resendAllFailed = async () => {
    if (!agreed) { setMsg("Please confirm the agreement above before sending codes."); return; }
    setResendingAll(true); setMsg("Re-sending all failed deliveries…");
    try {
      const { data } = await client.post("/licenses/resend-failed");
      setMsg(data.still_failing > 0
        ? `Re-sent ${data.attempted} code(s); ${data.still_failing} still failing — check Twilio/SendGrid setup and the recipient details.`
        : `✓ Re-sent ${data.attempted} code(s) — all delivered successfully.`);
      load();
    } catch (e) { setMsg(apiErr(e)); }
    finally { setResendingAll(false); }
  };

  const activate = async () => {
    if (!actCode) return;
    try { await client.post(`/licenses/${actFor}/activate`, { code: actCode }); setMsg("Activated — all features unlocked."); setActFor(null); setActCode(""); load(); }
    catch (e) { setMsg(apiErr(e)); }
  };

  const disable = async (id) => { await client.post(`/licenses/${id}/disable`); load(); };
  const del = async (id) => { if (window.confirm("Delete this license?")) { await client.delete(`/licenses/${id}`); load(); } };

  const [wipeFor, setWipeFor] = useState(null);   // app_type to wipe, or null
  const [wipeText, setWipeText] = useState("");
  const doWipe = async () => {
    if (wipeText.trim().toUpperCase() !== "DELETE") return;
    setMsg("");
    try {
      const { data } = await client.post(`/data/${wipeFor}/wipe`, { confirm: wipeText });
      setMsg(`Deleted ${data.deleted} record(s) of ${APP_FULL[wipeFor] || wipeFor} customer data. Your Client Profile is unchanged.`);
      setWipeFor(null); setWipeText("");
    } catch (e) { setMsg(apiErr(e)); }
  };

  const recover = async () => {
    setMsg("");
    try {
      const { data } = await client.post("/licenses/recover", { ...rec, first_ten: rec.first_ten || null });
      setMsg(`Recovered full code: ${data.full_code} — ${data.message}`);
      setRecOpen(false); load();
    } catch (e) { setMsg(apiErr(e)); }
  };

  const saveLock = async () => {
    try { await client.put(`/licenses/${lockFor}/lock`, { new_lock: newLock }); setMsg("Lock updated."); setLockFor(null); setNewLock(""); load(); }
    catch (e) { setMsg(apiErr(e)); }
  };

  const statusColor = (s) => ({ created: "neutral", first10_sent: "warning", activated: "success", recovered: "info", disabled: "danger" }[s] || "neutral");

  return (
    <div className="space-y-6 fadeup">
      <div className="flex flex-wrap items-center gap-3">
        <h1 className="font-head text-2xl sm:text-3xl">{APP_FULL.master_enabler}</h1>
        <Badge color="success" data-testid="master_enabler-version">v{APP_VERSIONS.master_enabler}</Badge>
        <span className="opacity-60">Generate & manage 20-character activation codes</span>
        <LiveBackupIndicator app="master" />
        <div className="ms-auto flex gap-2">
          {isAdmin && <Button variant="ghost" onClick={() => { setTestOpen(true); setTestResult(null); }} data-testid="open-test-sms"><MessageSquare size={16} /> Send test SMS</Button>}
          {can("edit_manual") && <Button variant="ghost" onClick={() => setEditorOpen(true)} data-testid="open-manual-editor"><Pencil size={16} /> Edit User Manual</Button>}
          <Button variant="ghost" onClick={() => setRecOpen(true)} data-testid="open-recover"><RotateCcw size={16} /> Recover</Button>
          {can("create_license") && <Button onClick={() => setOpen(true)} disabled={!agreed} title={!agreed ? "Confirm the agreement first" : ""} data-testid="open-create-license"><Plus size={16} /> New Instance</Button>}
        </div>
      </div>

      {!ownerView && (
        <Card className="text-sm opacity-80" data-testid="runner-notice">
          You are signed in as an application runner. You can activate and recover your own license codes here.
          Code generation, disclosure and instance management are reserved for the application owner.
        </Card>
      )}

      {msg && <Card className="border-[var(--primary)]/40 text-sm" data-testid="enabler-message">{msg}</Card>}

      {ownerView && (() => {
        const failed = list.filter((l) => l.delivery_status && ["sms", "email"].some((c) => l.delivery_status[c] === "fallback") && !l.disabled);
        if (failed.length === 0) return null;
        return (
          <Card className="border-[#DA1E28]/40 bg-[#DA1E28]/[0.05]" data-testid="delivery-health-strip">
            <div className="flex flex-wrap items-center gap-3">
              <AlertTriangle size={18} className="text-[#DA1E28]" />
              <div className="text-sm">
                <b className="text-[#DA1E28]" data-testid="delivery-health-count">{failed.length}</b> activation code {failed.length === 1 ? "delivery" : "deliveries"} did not send.
                <span className="opacity-70"> Check your Twilio/SendGrid setup or the recipient details, then re-send.</span>
              </div>
              <Button variant="danger" className="ms-auto" onClick={resendAllFailed} disabled={resendingAll || !agreed}
                title={!agreed ? "Confirm the agreement first" : "Re-send every failed delivery now"} data-testid="resend-all-failed">
                <Send size={15} /> {resendingAll ? "Re-sending…" : "Resend all failed"}
              </Button>
            </div>
          </Card>
        );
      })()}
      {ownerView && <TierRoster />}


      <Modal open={requireProfile} onClose={() => {}} title={tp("Complete your profile to continue", lang)} testid="require-profile-modal" wide>
        <div className="space-y-3">
          <div className="flex items-start gap-2 rounded-md bg-amber-500/10 p-3 text-sm text-amber-700 dark:text-amber-400">
            <ShieldCheck size={18} className="mt-0.5" />
            <span>{tp("Thanks for your purchase! Please complete your customer-service profile before using the apps. This helps our team support you accurately.", lang)}</span>
          </div>
          <ProfileForm initial={acct?.profile} initialConsents={acct?.consents}
            onSaved={() => client.get("/account/purchases").then((r) => setAcct(r.data))} />
        </div>
      </Modal>

      <Modal open={!!welcome} onClose={() => setWelcome(null)} title="Welcome to FopFo™ — you're all set!" testid="welcome-modal" wide>
        <div className="space-y-4">
          <div className="flex items-center gap-2 text-[var(--primary)]"><CheckCircle2 size={22} /> <span className="font-head text-lg">Payment successful</span></div>
          <p className="text-sm opacity-75">Here {welcome && welcome.licenses.length > 1 ? "are your activation codes" : "is your activation code"}. Keep {welcome && welcome.licenses.length > 1 ? "them" : "it"} safe — you'll enter {welcome && welcome.licenses.length > 1 ? "each one" : "it"} inside the app to unlock all features.</p>
          <div className="space-y-3">
            {welcome && welcome.licenses.map((l) => {
              const code = l.full_code || l.display_code || "";
              const sl = storeLinks[l.app_type] || {};
              return (
                <div key={l.id} className="rounded-md border border-black/10 dark:border-white/10 p-3" data-testid={`welcome-lic-${l.id}`}>
                  <div className="flex items-center justify-between gap-2">
                    <div className="font-medium">{APP_FULL[l.app_type] || l.app_type}</div>
                    <Badge color="success">PAID</Badge>
                  </div>
                  <div className="mt-2 flex items-center gap-2">
                    <code className="flex-1 rounded bg-black/[0.04] dark:bg-white/[0.06] px-3 py-2 font-mono text-sm tracking-wider" data-testid={`welcome-code-${l.id}`}>{code}</code>
                    <button onClick={() => copyCode(code)} data-testid={`welcome-copy-${l.id}`} className="rounded-md border border-black/15 dark:border-white/15 px-2 py-2 lift" title="Copy code">
                      {copied === code ? <Check size={15} /> : <Copy size={15} />}
                    </button>
                  </div>
                  {(sl.app_store || sl.play_store) && (
                    <div className="mt-2 flex flex-wrap gap-2">
                      {sl.app_store && <a href={sl.app_store} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 rounded-md border border-black/15 dark:border-white/15 px-2 py-1 text-xs lift"><Download size={13} /> App Store</a>}
                      {sl.play_store && <a href={sl.play_store} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 rounded-md border border-black/15 dark:border-white/15 px-2 py-1 text-xs lift"><Download size={13} /> Google Play</a>}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
          <div className="rounded-md bg-[var(--primary)]/5 p-3 text-sm">
            <div className="mb-1 font-medium">Next steps</div>
            <ol className="ms-5 list-decimal space-y-1 opacity-80">
              <li>Download the app for your device (links above where available), or use it right here on the web.</li>
              <li>Open the app and enter your 20-character code on the activation screen.</li>
              <li>All features unlock instantly — enjoy!</li>
            </ol>
          </div>
          <div className="flex justify-end"><Button onClick={() => setWelcome(null)} data-testid="welcome-done">Done</Button></div>
        </div>
      </Modal>

      <Modal open={reminderOpen} onClose={dismissReminder} title="Before you begin" testid="visit-reminder">
        <div className="space-y-3 text-sm">
          <p className="opacity-80">Please use the <b>same email address and mobile phone number</b> you originally used to obtain your first access code. They identify your account for codes, recovery and notifications.</p>
          <div className="flex justify-end"><Button onClick={dismissReminder} data-testid="visit-reminder-ok">Got it</Button></div>
        </div>
      </Modal>

      <Card className="space-y-4" data-testid="visitor-agreement">
        <div className="flex flex-wrap items-center gap-2">
          <BookOpen size={18} className="text-[var(--primary)]" />
          <span className="font-head text-lg">User Manual &amp; agreement</span>
          {manualVer && <Badge color="info">Manual v{manualVer.version} · {manualVer.last_modified?.slice(0, 10)}</Badge>}
          {agreed && <Badge color="success"><ShieldCheck size={12} className="inline" /> agreed this session</Badge>}
        </div>
        <p className="text-sm opacity-70">Review the User Manual (including Terms of Use and Privacy Policy) in your language, then confirm the statements below. These must be confirmed on each visit before any codes are sent or transmitted by text or email.</p>

        <div className="flex flex-wrap items-end gap-2">
          <div className="w-64"><Select label="Application manual" value={manualApp} onChange={(e) => setManualApp(e.target.value)} data-testid="va-manual-app">
            {APP_OPTIONS.map((o) => <option key={o.v} value={o.v}>{o.l}</option>)}
          </Select></div>
          <div className="w-48"><Select label="Language" value={manualLang} onChange={(e) => setManualLang(e.target.value)} data-testid="va-manual-lang">
            {LANGS.map((l) => <option key={l.code} value={l.code}>{`${l.code} · ${l.native}`}</option>)}
          </Select></div>
          <Button variant="subtle" onClick={() => openManual(manualApp, manualLang)} data-testid="va-view-manual"><FileText size={15} /> View manual</Button>
          <Button variant="ghost" onClick={() => downloadManual(manualApp, manualLang)} data-testid="va-download-manual"><FileText size={15} /> Download</Button>
        </div>

        <div className="space-y-2">
          <label className="flex items-start gap-2 text-sm">
            <input type="checkbox" className="mt-1" checked={termsChk} onChange={(e) => { setTermsChk(e.target.checked); setAgreed(false); }} data-testid="va-terms" />
            <span>I have read and accepted the terms of service and conditions in the User Manual.</span>
          </label>
          <label className="flex items-start gap-2 text-sm">
            <input type="checkbox" className="mt-1" checked={sysChk} onChange={(e) => { setSysChk(e.target.checked); setAgreed(false); }} data-testid="va-system" />
            <span>{systemConsentText}</span>
          </label>
        </div>

        {!ownerView && (
          <div className="space-y-2 rounded-lg border border-black/10 dark:border-white/10 p-3" data-testid="va-marketing">
            <div className="text-xs font-medium opacity-70">Marketing communications (please choose one):</div>
            <label className="flex items-start gap-2 text-sm">
              <input type="radio" name="marketing" className="mt-1" checked={marketing === true} onChange={() => { setMarketing(true); setAgreed(false); }} data-testid="va-marketing-yes" />
              <span>I agree to accept marketing communications from the vendor from time to time.</span>
            </label>
            <label className="flex items-start gap-2 text-sm">
              <input type="radio" name="marketing" className="mt-1" checked={marketing === false} onChange={() => { setMarketing(false); setAgreed(false); }} data-testid="va-marketing-no" />
              <span>I do not agree to accept marketing communications from the vendor from time to time.</span>
            </label>
          </div>
        )}

        <div className="flex items-center gap-3">
          <Button onClick={confirmConsent} disabled={!canConfirm || agreed} data-testid="va-confirm"><ShieldCheck size={15} /> {agreed ? "Confirmed" : "Confirm & continue"}</Button>
          {!agreed && <span className="text-xs opacity-60">Both statements are required{!ownerView ? " and a marketing choice" : ""} to proceed.</span>}
        </div>
      </Card>

      <SpecialManuals />

      {can("contacts") && <ContactsMessaging />}
      <ClientsSection />
      <ClusterInvites />
      {isAdmin && <OlosoPanel />}
      {isAdmin && <RepairDispatch />}
      {isAdmin && <DemoAccountsPanel />}
      <ManualEditorModal open={editorOpen} onClose={() => setEditorOpen(false)} />

      {ownerView && list.length > 0 && <StatusSummary list={list} />}

      <div className="grid gap-4">
        {list.length === 0 && <Card className="text-center opacity-60">{ownerView ? "No instances yet. Create one to generate a code." : "No instances have been assigned to you yet by the owner."}</Card>}
        {list.map((l) => (
          <Card key={l.id} className="space-y-3" data-testid={`license-${l.id}`}>
            <div className="flex flex-wrap items-center gap-3">
              {l.app_type === "benefits"
                ? <span className="font-head font-medium">SEBAA™</span>
                : <>
                    <img src={APP_LOGOS[l.app_type] || "/brand-logo.png"} alt="" className="h-8 w-8 object-contain" />
                    <img src={APP_WORDMARKS[l.app_type]} alt={APP_FULL[l.app_type] || l.app_type} className="h-6 w-auto object-contain" data-testid={`license-wordmark-${l.id}`} />
                  </>}
              <Badge color={statusColor(l.status)}>{l.status}</Badge>
              <Badge color="info">{l.language_code}·{l.os_code}·{l.device_class}</Badge>
              {l.activated && <Badge color="success"><ShieldCheck size={12} className="inline" /> unlocked</Badge>}
              {l.paid ? <Badge color="success">paid</Badge> : <Badge color="warning">unpaid</Badge>}
              {l.delivery_status && ["sms", "email"].some((c) => l.delivery_status[c] === "fallback") && (
                <>
                  <Badge color="danger" data-testid={`delivery-warn-${l.id}`} title="A code delivery did not actually send. Check the recipient number/email or the Twilio/SendGrid setup, then re-send.">
                    <AlertTriangle size={12} className="inline" /> {["sms", "email"].filter((c) => l.delivery_status[c] === "fallback").map((c) => c.toUpperCase()).join(" & ")} not delivered
                  </Badge>
                  <button onClick={() => resend(l.id)} data-testid={`resend-${l.id}`} title="Re-send the activation code now by SMS and email"
                    className="inline-flex items-center gap-1 rounded-full border border-[var(--primary)]/50 px-2 py-0.5 text-[11px] font-medium text-[var(--primary)] hover:bg-[var(--primary)]/10 lift">
                    <Send size={11} /> Resend
                  </button>
                </>
              )}
              <span className="ms-auto font-mono text-sm tracking-wider" data-testid={`code-${l.id}`}>
                {reveal[l.id] || l.display_code}
              </span>
            </div>
            <div className="text-xs opacity-70">{l.email} · {l.phone}{l.instance_name ? ` · "${l.instance_name}"` : ""}</div>
            {l.service_expires_at && (
              <div className="flex items-center gap-1.5 text-xs opacity-70" data-testid={`service-${l.id}`} title={"Covers: " + (l.service?.coverage || []).join("; ")}>
                {l.service?.reminder?.level && l.service.reminder.level !== "none" && (
                  <span data-testid={`service-flasher-${l.id}`}
                    className={`inline-block h-2 w-2 rounded-full animate-pulse ${l.service.reminder.level === "red" ? "bg-[#DA1E28]" : l.service.reminder.level === "yellow" ? "bg-amber-400" : "bg-emerald-500"}`} />
                )}
                Service: $7.50/mo (annually {cur} {l.service?.annual_total_usd?.toFixed(2)}, + tax at checkout) · {l.service?.reminder?.level === "red" ? `OVERDUE since ${new Date(l.service_expires_at).toLocaleDateString(undefined, { month: "long", year: "numeric" })}` : `renews by ${new Date(l.service_expires_at).toLocaleDateString(undefined, { month: "long", year: "numeric" })}`}
              </div>
            )}
            <div className="flex flex-wrap gap-2">
              {!l.paid && !l.disabled && (() => { const p = priceFor(l.app_type); return (
                <Button onClick={() => buy(l.id)} data-testid={`buy-${l.id}`} title={p ? `Price ${cur} ${p.total.toFixed(2)} · applicable tax added at checkout` : ""}>
                  <CreditCard size={15} /> Unlock — {cur} {p ? p.total.toFixed(2) : "…"}
                </Button>
              ); })()}
              {l.paid && !l.disabled && l.service?.reminder?.level && l.service.reminder.level !== "none" && (
                <Button variant={l.service.reminder.level === "red" ? "danger" : "subtle"}
                  onClick={() => renewService(l.id)} data-testid={`renew-${l.id}`}
                  title="Renew the annual service charge ($7.50/mo, billed annually; applicable tax added at checkout)">
                  <RotateCcw size={15} /> Renew service — {cur} {l.service?.annual_total_usd?.toFixed(2)}
                </Button>
              )}
              {can("reveal_full") && <Button variant="subtle" onClick={() => revealFull(l.id)} data-testid={`reveal-${l.id}`}>
                {reveal[l.id] ? <EyeOff size={15} /> : <Eye size={15} />} {reveal[l.id] ? "Hide" : "Reveal full 20"}
              </Button>}
              {can("disclose_first10") && <Button variant="subtle" onClick={() => disclose(l.id)} data-testid={`disclose-${l.id}`}><Send size={15} /> Resend first 10</Button>}
              {!l.activated && !l.disabled && <Button variant="subtle" onClick={() => { setActFor(l.id); setActCode(""); }} data-testid={`activate-${l.id}`}><ShieldCheck size={15} /> Activate</Button>}
              {can("change_lock") && <Button variant="subtle" onClick={() => setLockFor(l.id)} data-testid={`lock-${l.id}`}><Lock size={15} /> Change lock</Button>}
              {can("disable") && !l.disabled && <Button variant="ghost" onClick={() => disable(l.id)} data-testid={`disable-${l.id}`}>Disable</Button>}
              {l.activated && !l.disabled && <Button variant="ghost" onClick={() => { setWipeFor(l.app_type); setWipeText(""); }} data-testid={`wipe-open-${l.id}`} title="Delete all customer-loaded data for this application (keeps your Client Profile)"><Trash2 size={15} /> Delete data</Button>}
              {isAdmin && <Button variant="danger" onClick={() => del(l.id)} data-testid={`delete-${l.id}`}><Trash2 size={15} /></Button>}
            </div>
          </Card>
        ))}
      </div>

      <Modal open={open} onClose={() => setOpen(false)} title="New standalone instance" testid="create-license-modal">
        <div className="space-y-3">
          <Select label="Application" value={form.app_type} onChange={(e) => setForm({ ...form, app_type: e.target.value })} data-testid="form-app-type">
            {APP_OPTIONS.map((o) => <option key={o.v} value={o.v}>{o.l}</option>)}
          </Select>
          {priceFor(form.app_type) && (
            <div className="rounded-md bg-black/5 dark:bg-white/10 p-3 text-xs" data-testid="form-price-breakdown">
              Price to unlock: <b>{cur} {priceFor(form.app_type).total.toFixed(2)}</b> · applicable tax added at checkout
            </div>
          )}
          <Input label="Email address" type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="form-email" />
          <Input label="Mobile phone (e.g. +15551234567)" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} data-testid="form-phone" />
          <div className="grid grid-cols-2 gap-3">
            <Select label="Language" value={form.language_code} onChange={(e) => setForm({ ...form, language_code: e.target.value })} data-testid="form-language">
              {LANGS.map((l) => <option key={l.code} value={l.code}>{`${l.code} · ${l.native}`}</option>)}
            </Select>
            <Select label="Operating system" value={form.os_code} onChange={(e) => setForm({ ...form, os_code: e.target.value })} data-testid="form-os">
              {OS.map((o) => <option key={o.v} value={o.v}>{o.l}</option>)}
            </Select>
          </div>
          <Select label="Device class" value={form.device_class} onChange={(e) => setForm({ ...form, device_class: e.target.value })} data-testid="form-device">
            <option value="desktop">Desktop</option><option value="mobile">Mobile</option>
          </Select>
          <Input label="Instance / backup name (up to 50 alphanumeric)" value={form.instance_name} maxLength={50} onChange={(e) => setForm({ ...form, instance_name: e.target.value })} data-testid="form-name" />
          <div className="flex justify-end gap-2 pt-2">
            <Button variant="ghost" onClick={() => setOpen(false)}>Cancel</Button>
            <Button onClick={create} data-testid="submit-license">Generate code</Button>
          </div>
        </div>
      </Modal>

      <Modal open={recOpen} onClose={() => setRecOpen(false)} title="Recover code on a new machine" testid="recover-modal">
        <div className="space-y-3">
          <p className="text-sm opacity-70">Present the matching email + phone. The prior installation on the same device class will be disabled.</p>
          <Input label="Email" type="email" value={rec.email} onChange={(e) => setRec({ ...rec, email: e.target.value })} data-testid="rec-email" />
          <Input label="Phone" value={rec.phone} onChange={(e) => setRec({ ...rec, phone: e.target.value })} data-testid="rec-phone" />
          <Input label="First 10 characters (optional)" value={rec.first_ten} onChange={(e) => setRec({ ...rec, first_ten: e.target.value })} data-testid="rec-first10" />
          <Select label="Device class to recover" value={rec.device_class} onChange={(e) => setRec({ ...rec, device_class: e.target.value })} data-testid="rec-device">
            <option value="desktop">Desktop</option><option value="mobile">Mobile</option>
          </Select>
          <div className="flex justify-end gap-2 pt-2">
            <Button variant="ghost" onClick={() => setRecOpen(false)}>Cancel</Button>
            <Button onClick={recover} data-testid="submit-recover">Recover</Button>
          </div>
        </div>
      </Modal>

      <Modal open={testOpen} onClose={() => setTestOpen(false)} title="Send a test SMS" testid="test-sms-modal">
        <div className="space-y-3">
          <p className="text-sm opacity-70">Sends from +14244841914. Use a real mobile number that is different from the Twilio number.</p>
          <Input label="Recipient phone (e.g. +15551234567)" value={testPhone} onChange={(e) => setTestPhone(e.target.value)} data-testid="test-sms-phone" />
          {testResult && (
            <div className={`rounded-md p-3 text-sm ${testResult.status === "sent" ? "bg-[#24A148]/15 text-[#24A148]" : "bg-[#F1C21B]/20"}`} data-testid="test-sms-result">
              {testResult.status === "sent"
                ? `✓ Delivered via ${testResult.provider} to ${testResult.to}`
                : `Channel status: ${testResult.status}${testResult.preview ? ` — ${testResult.preview}` : ""}`}
            </div>
          )}
          <div className="flex justify-end gap-2 pt-1">
            <Button variant="ghost" onClick={() => setTestOpen(false)}>Close</Button>
            <Button onClick={sendTestSms} data-testid="submit-test-sms"><MessageSquare size={15} /> Send</Button>
          </div>
        </div>
      </Modal>

      <Modal open={!!actFor} onClose={() => setActFor(null)} title="Activate — enter full 20-character code" testid="activate-modal">
        <div className="space-y-3">
          <p className="text-sm opacity-70">Codes are case-sensitive. One-time input fully unlocks all features.</p>
          <Input label="Full 20-character code" maxLength={20} value={actCode} onChange={(e) => setActCode(e.target.value)} data-testid="activate-input" className="font-mono tracking-wider" />
          <div className="flex justify-end gap-2"><Button variant="ghost" onClick={() => setActFor(null)}>Cancel</Button><Button onClick={activate} data-testid="submit-activate">Unlock</Button></div>
        </div>
      </Modal>

      <Modal open={!!lockFor} onClose={() => setLockFor(null)} title="Change installed-app lock (5 chars)" testid="lock-modal">
        <div className="space-y-3">
          <Input label="New 5-character lock" maxLength={5} value={newLock} onChange={(e) => setNewLock(e.target.value)} data-testid="lock-input" />
          <div className="flex justify-end gap-2"><Button variant="ghost" onClick={() => setLockFor(null)}>Cancel</Button><Button onClick={saveLock} data-testid="submit-lock">Save</Button></div>
        </div>
      </Modal>

      <Modal open={!!wipeFor} onClose={() => setWipeFor(null)} title="Delete all customer data?" testid="wipe-modal">
        <div className="space-y-3">
          <div className="flex items-start gap-2 rounded-md border border-[#DA1E28]/30 bg-[#DA1E28]/[0.06] p-3 text-sm">
            <Trash2 size={18} className="mt-0.5 shrink-0 text-[#DA1E28]" />
            <p className="text-justify">This permanently deletes <b>all customer-loaded data</b> for <b>{APP_FULL[wipeFor] || wipeFor}</b>, but the <b>Client Profile will remain</b>. <b className="text-[#DA1E28]">This action cannot be reversed.</b></p>
          </div>
          <Input label="Type DELETE to confirm" value={wipeText} onChange={(e) => setWipeText(e.target.value)} data-testid="wipe-input" autoComplete="off" />
          <div className="flex justify-end gap-2">
            <Button variant="ghost" onClick={() => setWipeFor(null)} data-testid="wipe-cancel">No, cancel Action</Button>
            <Button variant="danger" disabled={wipeText.trim().toUpperCase() !== "DELETE"} onClick={doWipe} data-testid="wipe-confirm"><Trash2 size={15} /> Yes, Delete All</Button>
          </div>
        </div>
      </Modal>
    </div>
  );
}

const APP_TYPES = ["family_tree", "holdings", "currency", "benefits"];
const APP_SHORT = { family_tree: "Familigi™", benefits: "SEBAA™", currency: "Currencida™", holdings: "BoxAlong™" };

function StatusSummary({ list }) {
  const bucket = (l) => l.disabled ? "suspended" : (l.activated ? "active" : "inactive");
  const rows = APP_TYPES.map((t) => {
    const items = list.filter((l) => l.app_type === t);
    return {
      type: t,
      active: items.filter((l) => bucket(l) === "active").length,
      inactive: items.filter((l) => bucket(l) === "inactive").length,
      suspended: items.filter((l) => bucket(l) === "suspended").length,
      total: items.length,
    };
  });
  const sum = (k) => rows.reduce((a, r) => a + r[k], 0);
  return (
    <Card className="overflow-x-auto p-0" data-testid="license-status-summary">
      <table className="w-full text-sm">
        <thead><tr className="border-b border-black/10 dark:border-white/10">
          <th className="p-3 text-start font-medium opacity-70">Application</th>
          <th className="p-3 text-end font-medium opacity-70">Active</th>
          <th className="p-3 text-end font-medium opacity-70">Inactive</th>
          <th className="p-3 text-end font-medium opacity-70">Suspended</th>
          <th className="p-3 text-end font-medium opacity-70">Total</th>
        </tr></thead>
        <tbody>
          {rows.map((r) => (
            <tr key={r.type} className="border-b border-black/5 dark:border-white/5" data-testid={`status-row-${r.type}`}>
              <td className="p-3">{APP_SHORT[r.type]}</td>
              <td className="p-3 text-end"><span className="text-[#24A148]" data-testid={`active-${r.type}`}>{r.active}</span></td>
              <td className="p-3 text-end"><span className="opacity-70">{r.inactive}</span></td>
              <td className="p-3 text-end"><span className="text-[#DA1E28]">{r.suspended}</span></td>
              <td className="p-3 text-end font-medium">{r.total}</td>
            </tr>
          ))}
          <tr className="bg-black/[0.03] dark:bg-white/[0.03] font-medium">
            <td className="p-3">All applications</td>
            <td className="p-3 text-end text-[#24A148]">{sum("active")}</td>
            <td className="p-3 text-end">{sum("inactive")}</td>
            <td className="p-3 text-end text-[#DA1E28]">{sum("suspended")}</td>
            <td className="p-3 text-end">{sum("total")}</td>
          </tr>
        </tbody>
      </table>
    </Card>
  );
}
